// BL-177 Step 2j (C): the month grid in the Schedule send popup. A real grid (role="grid"): arrows move by day / week, Home / End to the
// start / end of the week, Page Up / Page Down by month, Enter or Space picks the focused day. Today is marked, days before today and
// beyond the 90-day limit are disabled (flat at 45%), the picked day is sunk and teal. Every day is a 3D pressable (Button3D).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button3D from '../ui/Button3D';
import { addDaysToDateKey, dateKey, parseDateKey } from '../../lib/scheduledSend';
import './SchedulePanel.css';

const WEEKDAYS = [['Su', 'Sunday'], ['Mo', 'Monday'], ['Tu', 'Tuesday'], ['We', 'Wednesday'], ['Th', 'Thursday'], ['Fr', 'Friday'], ['Sa', 'Saturday']];
const MONTH_LABEL = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const DAY_LABEL = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

const utc = ({ year, month, day }) => new Date(Date.UTC(year, month - 1, day));
const weekdayOf = (parts) => utc(parts).getUTCDay();
const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const monthKey = ({ year, month }) => year * 12 + (month - 1);
const clamp = (key, first, last) => (key < first ? first : key > last ? last : key);

/** Weeks of the month: arrays of 7 cells, each a date key or null (a blank before the 1st / after the last day). */
export function monthWeeks(year, month) {
    const lead = weekdayOf({ year, month, day: 1 });
    const cells = [...Array(lead).fill(null), ...Array.from({ length: daysInMonth(year, month) }, (_, index) => dateKey({ year, month, day: index + 1 }))];
    while (cells.length % 7) cells.push(null);
    return Array.from({ length: cells.length / 7 }, (_, week) => cells.slice(week * 7, week * 7 + 7));
}

export default function ScheduleCalendar({ value, range, today, onSelect, focusRef, idPrefix = 'sched-cal' }) {
    const first = range.first;
    const last = range.last;
    const selected = parseDateKey(value) ? value : '';
    const start = clamp(selected || first, first, last);
    const [view, setView] = useState(() => parseDateKey(start));
    const [focusKey, setFocusKey] = useState(start);
    const pendingFocus = useRef('');
    const gridRef = useRef(null);

    // the picked day changes from outside (a different time zone, a re-opened edit): follow it
    useEffect(() => {
        if (!selected) return;
        const parts = parseDateKey(selected);
        setView((current) => (current && current.year === parts.year && current.month === parts.month ? current : { year: parts.year, month: parts.month }));
        setFocusKey(selected);
    }, [selected]);
    // the allowed range moves (a new zone): keep the roving focus on a day that can be picked
    useEffect(() => { setFocusKey((current) => clamp(current, first, last)); }, [first, last]);
    useEffect(() => {
        if (!pendingFocus.current) return;
        const node = gridRef.current?.querySelector(`button[data-date="${pendingFocus.current}"]`);
        pendingFocus.current = '';
        if (node) node.focus();
    });

    const weeks = useMemo(() => monthWeeks(view.year, view.month), [view.year, view.month]);
    const firstParts = parseDateKey(first);
    const lastParts = parseDateKey(last);
    // the one day in the grid that Tab reaches: the focused day when it is on this page, else the first day here that can be picked
    const pickable = (key) => key >= first && key <= last;
    const inView = (key) => { const parts = parseDateKey(key); return Boolean(parts) && parts.year === view.year && parts.month === view.month; };
    const rovingKey = inView(focusKey) && pickable(focusKey)
        ? focusKey
        : weeks.flat().find((key) => key && pickable(key)) || '';
    const canGoBack = monthKey(view) > monthKey(firstParts);
    const canGoForward = monthKey(view) < monthKey(lastParts);
    const moveMonth = (delta) => {
        const moved = new Date(Date.UTC(view.year, view.month - 1 + delta, 1));
        setView({ year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1 });
    };

    const goTo = (key) => {
        const target = clamp(key, first, last);
        const parts = parseDateKey(target);
        pendingFocus.current = target;
        setFocusKey(target);
        setView({ year: parts.year, month: parts.month });
    };
    const onKeyDown = (event) => {
        const key = event.target?.dataset?.date;
        const parts = key && parseDateKey(key);
        if (!parts) return;
        let next = null;
        switch (event.key) {
            case 'ArrowLeft': next = addDaysToDateKey(key, -1); break;
            case 'ArrowRight': next = addDaysToDateKey(key, 1); break;
            case 'ArrowUp': next = addDaysToDateKey(key, -7); break;
            case 'ArrowDown': next = addDaysToDateKey(key, 7); break;
            case 'Home': next = addDaysToDateKey(key, -weekdayOf(parts)); break;
            case 'End': next = addDaysToDateKey(key, 6 - weekdayOf(parts)); break;
            case 'PageUp':
            case 'PageDown': {
                const delta = event.key === 'PageUp' ? -1 : 1;
                const moved = new Date(Date.UTC(parts.year, parts.month - 1 + delta, 1));
                const y = moved.getUTCFullYear();
                const m = moved.getUTCMonth() + 1;
                next = dateKey({ year: y, month: m, day: Math.min(parts.day, daysInMonth(y, m)) });
                break;
            }
            case 'Enter':
            case ' ':
                // a real button already does this natively; done here too so the key never reaches the composer behind the popup
                event.preventDefault();
                event.stopPropagation();
                if (key >= first && key <= last) { setFocusKey(key); onSelect?.(key); }
                return;
            default: return;
        }
        event.preventDefault();
        event.stopPropagation();
        goTo(next);
    };

    const titleId = `${idPrefix}-month`;
    return (
        <div className="ia-sched-cal" data-schedule-calendar="true">
            <div className="ia-sched-cal__head">
                <Button3D className="ia-sched-cal__nav" aria-label="Previous month" disabled={!canGoBack} onClick={() => moveMonth(-1)}>
                    <span aria-hidden="true">‹</span>
                </Button3D>
                <h3 id={titleId} className="ia-sched-cal__month" aria-live="polite" data-schedule-month="true">{MONTH_LABEL.format(utc({ ...view, day: 1 }))}</h3>
                <Button3D className="ia-sched-cal__nav" aria-label="Next month" disabled={!canGoForward} onClick={() => moveMonth(1)}>
                    <span aria-hidden="true">›</span>
                </Button3D>
            </div>
            <table ref={gridRef} role="grid" aria-labelledby={titleId} className="ia-sched-cal__grid" onKeyDown={onKeyDown}>
                <thead>
                    <tr>
                        {WEEKDAYS.map(([short, long]) => <th key={short} scope="col"><abbr title={long}>{short}</abbr></th>)}
                    </tr>
                </thead>
                <tbody>
                    {weeks.map((week, row) => (
                        <tr key={row}>
                            {week.map((key, column) => {
                                if (!key) return <td key={column} aria-hidden="true" />;
                                const disabled = key < first || key > last;
                                const isSelected = key === selected;
                                const isToday = key === today;
                                const roving = key === rovingKey;
                                const parts = parseDateKey(key);
                                return (
                                    <td key={column} role="gridcell" aria-selected={isSelected}>
                                        <Button3D
                                            ref={roving ? focusRef : undefined}
                                            variant={isSelected ? 'go' : 'primary'}
                                            className={`ia-sched-day${isToday ? ' ia-sched-day--today' : ''}${isSelected ? ' ia-sched-day--picked' : ''}`}
                                            data-date={key}
                                            data-today={isToday ? 'true' : undefined}
                                            aria-pressed={isSelected}
                                            aria-label={`${DAY_LABEL.format(utc(parts))}${isToday ? ', today' : ''}`}
                                            aria-current={isToday ? 'date' : undefined}
                                            tabIndex={roving ? 0 : -1}
                                            disabled={disabled}
                                            onClick={() => { setFocusKey(key); onSelect?.(key); }}
                                        >
                                            {parts.day}
                                        </Button3D>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
